import { FeatureCollection } from "geojson";

import { groupFilesByDataset } from "./fileBundles";
import { describeGdalFailure, GdalLoadError } from "./gdalErrors";
import type { Gdal } from "./gdalRuntime";
import { loadGdal } from "./loadGdal";
import { isGeoJsonFile, readGeoJsonFile } from "./readGeoJsonFile";

/** The step a conversion is currently on. */
export type ConversionStage = "loading-converter" | "converting";

export interface ConvertFilesOptions {
  onStageChange?: (stage: ConversionStage) => void;
}

/** Describes a dataset GDAL opened, for diagnosing a failed conversion. */
export interface GdalDatasetSummary {
  driver?: string;
  layers?: string;
  featureCount?: number;
  crs?: string;
}

/** Thrown when GDAL cannot read or convert the given files. */
export class GdalConversionError extends Error {
  constructor(
    public readonly detail: string,
    public readonly context: { files: string[]; stage: "open" | "convert"; dataset?: GdalDatasetSummary }
  ) {
    super(`GDAL failed to ${context.stage} ${context.files.join(", ")}: ${detail}`);
    this.name = "GdalConversionError";
  }
}

type OpenResult = Awaited<ReturnType<Gdal["open"]>>;
type Dataset = OpenResult["datasets"][number];

/** The subset of the ogrinfo JSON report that gdal3.js attaches to each opened dataset. */
interface OgrInfo {
  driverShortName?: string;
  layers?: {
    name: string;
    featureCount?: number;
    geometryFields?: { coordinateSystem?: { projjson?: { id?: { authority: string; code: number | string } }; wkt?: string } }[];
  }[];
}

const describeCrs = (layers: NonNullable<OgrInfo["layers"]>): string => {
  const crs = layers[0]?.geometryFields?.[0]?.coordinateSystem;
  if (crs?.projjson?.id) return `${crs.projjson.id.authority}:${crs.projjson.id.code}`;
  return crs?.wkt?.slice(0, 120) ?? "(none)";
};

const summarise = ({ info }: Dataset): GdalDatasetSummary => {
  if (!info) return {};

  const { driverShortName, layers = [] } = info as OgrInfo;
  return {
    driver: driverShortName,
    layers: layers.map((layer) => layer.name).join(", "),
    featureCount: layers.reduce((total, layer) => total + (layer.featureCount ?? 0), 0),
    crs: describeCrs(layers),
  };
};

/** Converts the files of one dataset into GeoJSON. */
const convertBundle = async (gdal: Gdal, files: File[]): Promise<FeatureCollection> => {
  const names = files.map((file) => file.name);

  let opened: OpenResult;
  try {
    // gdal3.js mounts an array of File objects, but only declares FileList in its types.
    opened = await gdal.open(files as unknown as FileList);
  } catch (reason) {
    throw new GdalConversionError(describeGdalFailure(reason), { files: names, stage: "open" });
  }

  const [dataset] = opened.datasets;
  if (!dataset) {
    throw new GdalConversionError(describeGdalFailure(opened.errors), { files: names, stage: "open" });
  }

  try {
    const output = await gdal.ogr2ogr(dataset, ["-f", "GeoJSON", "-t_srs", "EPSG:4326"], "converted");
    const bytes = await gdal.getFileBytes(output);
    return JSON.parse(new TextDecoder().decode(bytes)) as FeatureCollection;
  } catch (reason) {
    throw new GdalConversionError(describeGdalFailure(reason), { files: names, stage: "convert", dataset: summarise(dataset) });
  } finally {
    await gdal.close(dataset);
  }
};

const isConverted = (collection: FeatureCollection | undefined): collection is FeatureCollection => collection !== undefined;

/**
 * Converts dropped GIS files into GeoJSON, one collection per dataset.
 *
 * Files belonging to a multi-file format are recognised by their shared basename,
 * so a shapefile or MapInfo bundle yields a single collection. Plain WGS84 GeoJSON
 * is read directly; everything else is converted by GDAL, downloaded on first use.
 */
export const convertFilesToGeoJson = async (files: File[], { onStageChange }: ConvertFilesOptions = {}): Promise<FeatureCollection[]> => {
  const bundles = Object.values(groupFilesByDataset(files));

  const direct = await Promise.all(
    bundles.map((bundle) => (bundle.length === 1 && isGeoJsonFile(bundle[0]) ? readGeoJsonFile(bundle[0]) : undefined))
  );
  if (direct.every(isConverted)) return direct;

  onStageChange?.("loading-converter");
  const gdal = await loadGdal().catch((reason) => {
    throw new GdalLoadError(reason);
  });

  onStageChange?.("converting");

  // load each bundle separately so gdal3.js doesn't get confused
  const collections: FeatureCollection[] = [];
  for (const [index, bundle] of bundles.entries()) {
    collections.push(direct[index] ?? (await convertBundle(gdal, bundle)));
  }
  return collections;
};
